import React, { useState, useEffect } from "react";
import {
  Card,
  Container,
  Spinner,
  Button,
  Form,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FaUserCircle, FaCamera } from "react-icons/fa";
import API_BASE_URL, { API_ENDPOINTS } from '../../../../config/api';
import { isPendingUser, isReadOnlyMode } from "../../../../utils/userPermissions";
import imageCompression from "browser-image-compression";
import ImageCropModal from "../../../common/ImageCropModal";

const getProfilePictureSrc = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
    return path;
  }
  return `${API_BASE_URL}/${path}`;
};

const Profile = () => {
  const { uid } = useParams();
  const [errors, setErrors] = useState({ mobileNo: "" }); //  Added to track mobile number validation
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availReasonPreset, setAvailReasonPreset] = useState("");
  const [availReasonCustom, setAvailReasonCustom] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    mobileNo: "",
    type: "",
    interest: "",
    profilePicture: "",
  });

  // Helper function to get initials from name
  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  // Helper function to generate a background color based on name
  const getInitialsBgColor = (name) => {
    if (!name) return "#6c757d";
    const colors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6", "#1abc9c", "#34495e"];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(
          API_ENDPOINTS.USERS.DISPLAY(uid)
        );
        setUser(userRes.data);
        setIsAvailable(userRes.data.isAvailable !== undefined ? userRes.data.isAvailable : true);
        setFormData({
          name: userRes.data.name || "",
          email: userRes.data.email || "",
          address: userRes.data.address || "",
          mobileNo: userRes.data.mobileNo || "",
          type: userRes.data.type || "",
          interest: userRes.data.interests || "",
          profilePicture: userRes.data.profilePhoto || userRes.data.profilePicture || "",
        });
      } catch {
        alert("Failed to load user info.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [uid]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("Please upload an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setRawImageSrc(reader.result);
      setShowCropModal(true);
      setPhotoError("");
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedBase64) => {
    setFormData((prev) => ({ ...prev, profilePicture: croppedBase64 }));
    setShowCropModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('profilePicture', file);

    try {
      const response = await axios.post(
        API_ENDPOINTS.USERS.UPLOAD_PROFILE_PICTURE(uid),
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      if (response.data && response.data.profilePicture) {
        setFormData((prev) => ({ ...prev, profilePicture: response.data.profilePicture }));
        alert("Profile picture uploaded successfully!");
      }
    } catch (err) {
      console.error("Profile picture upload error:", err);
      alert(err.response?.data?.message || "Failed to upload profile picture");
    }
  };

  const handleAvailabilityToggleClick = () => {
    if (isPendingUser()) return;
    setAvailReasonPreset("");
    setAvailReasonCustom("");
    setShowAvailabilityModal(true);
  };

  const confirmAvailabilityChange = async () => {
    const finalReason = availReasonPreset === "Other" ? availReasonCustom.trim() : availReasonPreset;

    if (!finalReason) {
      alert("Please provide a reason.");
      return;
    }

    try {
      const response = await axios.patch(
        API_ENDPOINTS.VOLUNTEERS.TOGGLE_AVAILABILITY(uid),
        { reason: finalReason, changedBy: "volunteer" }
      );
      setIsAvailable(response.data.isAvailable);
      setShowAvailabilityModal(false);
      alert(`Your availability status has been successfully updated to: ${response.data.isAvailable ? "ACTIVE" : "INACTIVE"}`);
    } catch (err) {
      console.error("Failed to update availability status:", err);
      alert(err.response?.data?.message || "Failed to update status. Please try again.");
    }
  };

const handleEditToggle = async () => {
  if (isEditing) {
    // ✅ Prevent saving if mobile number is not exactly 10 digits
    if (formData.mobileNo.length !== 10) {
      alert("Mobile number must be exactly 10 digits");
      return;
    }

    try {
      await axios.put(
        API_ENDPOINTS.USERS.UPDATE(uid),
        {
          name: formData.name,
          email: formData.email,
          address: formData.address,
          mobileNo: formData.mobileNo,
          type: formData.type,
          interests: formData.interest,
          profilePhoto: formData.profilePicture || null,
        }
      );
      setUser({ ...user, ...formData });
      alert("Profile updated successfully!");
    } catch {
      alert("Failed to update profile.");
    }
  }
  setIsEditing(!isEditing);
};


  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  const fieldBoxStyle = {
    padding: "12px 16px",
    border: "none",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    borderRadius: "6px",
  };

  const inputStyle = {
    border: "none",
    outline: "none",
    boxShadow: "none",
    fontSize: "1rem",
    backgroundColor: "transparent",
    paddingLeft: "0",
  };

  const readOnlyInputStyle = {
    ...inputStyle,
    color: "#6c757d",
    cursor: "not-allowed",
  };

  return (
    <div className="main-content">

      <div className="text-center px-4 pt-4">
        <img src="/images/LOGO.png" alt="TMGF" style={{ width: 100 }} />
        <h3 style={{ fontWeight: "bold", marginTop: 12, marginBottom: 20, color: "#ac2e62" }}>
          The Mother Global Foundation
        </h3>
      </div>

      <Container className="px-4 py-3">
        <div className="db1-glass-card" style={{ padding: '20px' }}>
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h4
              className="mb-0 d-flex align-items-center"
              style={{
                background: "white",
                color: "#a70a4a",
                padding: "10px 24px",
                fontSize: "1.5rem",
                fontWeight: "bold",
                borderRadius: "10px",
                display: "inline-flex",
                alignItems: "center",
                border: "2px solid #8ee8ff",
                maxWidth: "290px",
              }}
            >
              <FaUserCircle size={24} style={{ marginRight: "10px", color: "#a70a4a" }} />
              Personal Details
            </h4>

            {/* Availability Toggle Switch */}
            <div style={{
              background: "white",
              padding: "8px 20px",
              borderRadius: "10px",
              border: "2px solid #8ee8ff",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
            }}>
              <span style={{ fontWeight: "bold", fontSize: "0.95rem", color: "#555" }}>Availability Status:</span>
              <Form.Check 
                type="switch"
                id="availability-switch"
                label={
                  <span style={{ 
                    fontWeight: "700", 
                    fontSize: "0.95rem", 
                    color: isAvailable ? "#198754" : "#dc2626"
                  }}>
                    {isAvailable ? "Active" : "Inactive"}
                  </span>
                }
                checked={isAvailable}
                disabled={isPendingUser()}
                onChange={handleAvailabilityToggleClick}
                title={isPendingUser() ? "Account pending approval - availability toggle disabled" : "Toggle availability"}
                style={{ cursor: isPendingUser() ? "not-allowed" : "pointer" }}
              />
            </div>
          </div>

          <Row>
            {/* Profile Picture Section */}
            <Col md={12} className="mb-4 text-center">
              <div style={{
                display: 'inline-block',
                position: 'relative',
                marginBottom: '20px',
                cursor: isEditing ? 'pointer' : 'default'
              }}>
                {formData.profilePicture ? (
                  <img
                    src={getProfilePictureSrc(formData.profilePicture)}
                    alt="Profile"
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '4px solid #a70a4a',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentNode.querySelector('.profile-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="profile-fallback"
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    backgroundColor: getInitialsBgColor(formData.name),
                    border: '4px solid #a70a4a',
                    display: formData.profilePicture ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: 'white',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    margin: '0 auto'
                  }}
                >
                  {getInitials(formData.name)}
                </div>

                {isEditing && (
                  <label
                    htmlFor="profile-upload"
                    style={{
                      position: 'absolute',
                      bottom: '5px',
                      right: '5px',
                      backgroundColor: '#a70a4a',
                      color: 'white',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '2px solid white',
                      transition: 'all 0.2s'
                    }}
                    title="Upload profile picture"
                  >
                    <FaCamera size={14} />
                    <input
                      type="file"
                      id="profile-upload"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </label>
                )}
              </div>
              {isEditing && (
                <div className="mt-2">
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ maxWidth: 220, margin: "0 auto", fontSize: "0.85rem" }}
                  />
                  {photoError && <small style={{ color: "red" }}>{photoError}</small>}
                </div>
              )}
            </Col>

            <Col md={6} className="mb-3">
              <div style={fieldBoxStyle}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "bold" }}>Name</Form.Label>
                  <Form.Control
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    style={inputStyle}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <div style={fieldBoxStyle}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "bold" }}>
                    Address
                  </Form.Label>
                  <Form.Control
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    style={inputStyle}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <div style={fieldBoxStyle}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "bold" }}>Email</Form.Label>
                  <Form.Control
                    name="email"
                    type="email"
                    value={formData.email}
                    readOnly={true}
                    style={readOnlyInputStyle}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <div style={fieldBoxStyle}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "bold" }}>
                    Contact No
                  </Form.Label>
                  <Form.Control
                    name="mobileNo"
                    type="tel"
                    value={formData.mobileNo}
                    readOnly={true}
                    style={readOnlyInputStyle}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <div style={fieldBoxStyle}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "bold" }}>Type</Form.Label>
                  <Form.Control
                    name="type"
                    type="text"
                    value={formData.type}
                    readOnly={true}
                    style={readOnlyInputStyle}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <div style={fieldBoxStyle}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "bold" }}>
                    Interest
                  </Form.Label>
                  <Form.Control
                    name="interest"
                    type="text"
                    value={formData.interest}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    style={inputStyle}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>

          <div className="text-center mt-3">
            {isPendingUser() && (
              <div className="alert alert-warning mb-3" role="alert">
                <i className="fa fa-exclamation-triangle me-2"></i>
                Your account is pending approval. Profile editing is disabled until approved.
              </div>
            )}
            <Button
              variant="primary"
              onClick={handleEditToggle}
              disabled={isPendingUser()}
              style={{ 
                backgroundColor: isPendingUser() ? "#6c757d" : "#15b1d3", 
                borderRadius: "8px",
                opacity: isPendingUser() ? 0.7 : 1 
              }}
              title={isPendingUser() ? "Account pending approval - Read-only access" : "Edit profile details"}
            >
              {isPendingUser() ? "Read Only Mode" : (isEditing ? "Save Details" : "Edit Details")}
            </Button>
          </div>
        </div>
      </Container>

      {/* Availability Status Change Modal */}
      <Modal show={showAvailabilityModal} onHide={() => setShowAvailabilityModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isAvailable ? "Mark Yourself as Inactive" : "Mark Yourself as Active"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You are about to change your availability status to{" "}
            <strong>{isAvailable ? "Inactive" : "Active"}</strong>.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Select Reason</Form.Label>
            <Form.Select 
              value={availReasonPreset} 
              onChange={(e) => setAvailReasonPreset(e.target.value)}
            >
              <option value="">-- Choose a reason --</option>
              {isAvailable ? (
                <>
                  <option value="Academic constraints/Exams">Academic constraints/Exams</option>
                  <option value="Temporary personal break">Temporary personal break</option>
                  <option value="Work/Job commitments">Work/Job commitments</option>
                  <option value="Moving away / Relocation">Moving away / Relocation</option>
                  <option value="Other">Other</option>
                </>
              ) : (
                <>
                  <option value="Resuming volunteering activities">Resuming volunteering activities</option>
                  <option value="Schedule cleared / Free time">Schedule cleared / Free time</option>
                  <option value="Other">Other</option>
                </>
              )}
            </Form.Select>
          </Form.Group>
          {availReasonPreset === "Other" && (
            <Form.Group className="mb-3">
              <Form.Label>Specify Custom Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={availReasonCustom}
                onChange={(e) => setAvailReasonCustom(e.target.value)}
                placeholder="Enter your reason..."
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAvailabilityModal(false)}>Cancel</Button>
          <Button
            variant={isAvailable ? "danger" : "success"}
            onClick={confirmAvailabilityChange}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    <ImageCropModal
        show={showCropModal}
        imageSrc={rawImageSrc}
        onCancel={() => setShowCropModal(false)}
        onCropComplete={handleCropComplete}
      />
    </div>

  );
};

export default Profile;